import React from 'react';
import { usePreviewSubscription } from 'content/sanity';
import { getClient } from 'content/sanity.server';
import { PortableText, toPlainText } from '@portabletext/react';
import { NextSeo } from 'next-seo';
import { getSanityImageData } from 'content/SanityImage';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/sl';
import { authorSlugsQuery, singleArticleQuery, singleAuthorQuery } from 'content/queries';
import { filterDataToSingleItem } from 'content/helpers';
import Footer from 'components/Footer';
import Author from 'models/articles/Author';
import Image from 'next/image';
import portableTextComponents from 'content/components';
import CardBase from 'components/cards/CardBase';
import DividerCurve from 'components/curves/DividerCurve';
import RelatedArticles from 'components/articles/RelatedArticles';
import FooterThinCurveSvg from 'components/curves/svg/FooterThinCurveSvg';
dayjs.locale('sl');
dayjs.extend(relativeTime);

export async function getStaticPaths() {
    const pages = await getClient(true).fetch(authorSlugsQuery);

    const paths = pages.map((slug) => ({
        params: {
            slug
        }
    }));

    return {
        paths,
        fallback: true
    };
}

export async function getStaticProps({ params, preview = false }) {
    const queryParams = { slug: params.slug };
    const data = await getClient(preview).fetch(singleAuthorQuery, queryParams);

    if (!data || data?.length === 0) return { notFound: true };

    const page = filterDataToSingleItem(data, preview);

    return {
        props: {
            preview,
            data: {
                page,
                recentArticles: page.recentArticles,
                query: singleArticleQuery,
                queryParams
            }
        }
    };
}

export default function AuthorPage({ data, preview }) {
    const { data: previewData } = usePreviewSubscription(data?.query, {
        params: data?.queryParams ?? {},
        initialData: data?.page,
        enabled: preview
    });

    const author: Author = filterDataToSingleItem(previewData, preview);

    if (!author) return null;
    const authorImage = getSanityImageData({ image: author.image, height: 100, width: 100 });

    return (
        <div className="single-author pt-6 md:pt-12">
            <NextSeo title={author.name} description={toPlainText(author.bio)} />
            <div className="container">
                <div className="flex flex-col lg:flex-row gap-8 items-start">
                    <div className="max-w-2xl">
                        <div className="flex items-start gap-8">
                            <Image
                                src={authorImage.url}
                                width={100}
                                height={100}
                                alt={author.name}
                                priority
                                quality={100}
                            />
                            <div>
                                <h1>{author.name}</h1>
                                {author?.location && <strong>{author.location}</strong>}
                                <p className="text-sm text-secondary mt-4">{author.articleCount} namigov</p>
                            </div>
                        </div>
                        <div className="mt-6">
                            {author?.bio && <PortableText value={author.bio} components={portableTextComponents} />}
                        </div>
                    </div>
                    {author?.favourites && (
                        <div className="w-[380px] max-w-full">
                            <CardBase>
                                <div className="p-4">
                                    <h3 className="text-brand">Moje najljubše stvari</h3>
                                    <div className="grid grid-cols-2 mt-4 gap-2">
                                        {author.favourites?.map((fav) => (
                                            <>
                                                <span>{fav.favourite_key}</span>
                                                <strong>{fav.favourite_value}</strong>
                                            </>
                                        ))}
                                    </div>
                                </div>
                            </CardBase>
                        </div>
                    )}
                </div>
            </div>

            {author?.articles?.length > 0 && (
                <section id="related" className="pb-32 bg-gray-200 relative mt-12">
                    <DividerCurve styles="absolute w-full left-0 -top-2" />
                    <RelatedArticles articles={author.articles} title={'Avtorjevi namigi'} />
                    <div className="my-4">
                        <FooterThinCurveSvg />
                    </div>
                    {data?.recentArticles?.length > 0 && (
                        <RelatedArticles articles={data?.recentArticles} title={'Najnovejši namigi'} />
                    )}
                </section>
            )}

            <section id="footer">
                <Footer />
            </section>
        </div>
    );
}
